import { useCallback, useSyncExternalStore } from 'react';

/**
 * What the sidebar shows while the side panel's tab is an app with navigation (or an app's home is
 * on screen): the conversations, or one of the app's sections (its id). It follows what is shown
 * (`key`: the app's tab, or the home): another tab, a closed panel or no app at all start again
 * from the default, Conversations in a conversation (an app never takes the sidebar by itself).
 */
export const CONVERSATIONS_SIDE = 'conversations';
export type SidebarSide = string;

let current: { key: string; side: SidebarSide } | null = null;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function sidebarSide(key: string | null, fallback: SidebarSide): SidebarSide {
  return key !== null && current?.key === key ? current.side : fallback;
}

function setSidebarSide(key: string, side: SidebarSide): void {
  if (current?.key === key && current.side === side) return;
  current = { key, side };
  for (const listener of listeners) listener();
}

/** Forgets the choice (tests). */
export function resetSidebarSides(): void {
  current = null;
  for (const listener of listeners) listener();
}

/**
 * The side the sidebar shows for what is on screen (`key`; none: nothing but the conversations),
 * `fallback` until the user picks one, and how to change it.
 */
export function useSidebarSide(
  key: string | null,
  fallback: SidebarSide = CONVERSATIONS_SIDE,
): readonly [SidebarSide, (side: SidebarSide) => void] {
  const read = (): SidebarSide => (key === null ? CONVERSATIONS_SIDE : sidebarSide(key, fallback));
  const side = useSyncExternalStore(subscribe, read, read);
  const change = useCallback(
    (next: SidebarSide) => {
      if (key !== null) setSidebarSide(key, next);
    },
    [key],
  );
  return [side, change] as const;
}
